import React from "react";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import s from "./TransactionDetailCard.module.scss";
import {
  checkMoneyDataForTable,
  getCardTypeIcon,
  dateTimeFormatter,
  hasPassedNextDayMidnight,
} from "utils/utils";
import { useTranslation } from "react-i18next";
import PaymentDetailsGrid from "../payment-detail-grid/PaymentDetailsGrid";
import responseCodeMapping from "utils/response_code_mapping.json";
import { Stack, Typography } from "@mui/material";
import PaymentDetailTag from "../payment-detail-tag/PaymentDetailTag";
import moment from "moment";

const cc = require("currency-codes");

const TransactionDetailCard = ({
  offline,
  online,
  dialog,
  transaction,
  payment,
  openSendReceiptDialog,
  openVoidTransactionDialog,
  openRefundTransactionDialog,
  closeDialog,
  openMainPayment,
}) => {
  const { t } = useTranslation();

  const isOffline = offline || !online;
  const ENV_LOCALE_KEY = isOffline ? "offline" : "online";
  const COMPONENT_LOCALE_KEY = `page.transactions.sharedComponents.transactionDetailsCard.${ENV_LOCALE_KEY}.`;

  const getCardIconComponent = (transaction, styles = {}) => {
    const cardType = transaction?.cardType ?? "";
    const cardIcon = getCardTypeIcon(cardType);
    return cardIcon ? (
      <div>
        <img
          src={cardIcon}
          style={{ verticalAlign: "bottom", ...styles }}
          height={24}
          alt={cardType.toUpperCase()}
        />
      </div>
    ) : (
      "-"
    );
  };

  const getTransactionDetailContent = (transaction, payment) => {
    const {
      paymentId,
      order_id,
      description,
      currency,
      paymentGateway,
      payment_gateway,
      cardType,
      first6Last4,
      entryMode,
      batchNumber,
      rrn,
      stan,
      terminalMid,
      terminalTid,
      time,
      aid,
      authCode,
      responseCode,
      employeeId,
      merchantId,
    } = transaction;

    if (isOffline) {
      //========================== Offline ==========================
      return (
        <PaymentDetailsGrid
          items={[
            ...(!!openMainPayment
              ? [
                  {
                    key: t(`${COMPONENT_LOCALE_KEY}content.paymentId`),
                    value: (
                      <div className={s.actionBtnContainer}>
                        <button
                          onClick={() => {
                            openMainPayment(paymentId);
                            !!closeDialog && closeDialog();
                          }}
                        >
                          {paymentId}
                        </button>
                      </div>
                    ),
                  },
                ]
              : []),
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.amount`),
              value: `${
                cc.number(currency ?? "")?.code ?? ""
              } ${checkMoneyDataForTable(transaction, "amount")}`,
            },
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.transactionTime`),
              value: dateTimeFormatter(time),
            },
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.responseCode`),
              value:
                cardType === "OCTOPUS"
                  ? "SUCCESS"
                  : `${responseCode ?? ""}${
                      responseCodeMapping[responseCode]?.["en"]
                        ? ` (${responseCodeMapping[responseCode]?.["en"]})`
                        : ""
                    }`,
            },
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.mid`),
              value: terminalMid,
            },
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.tid`),
              value: terminalTid,
            },
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.employeeId`),
              value: employeeId,
            },
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.merchantId`),
              value: merchantId,
            },
            {
              ...(paymentGateway === "OCTOPUS_HKBN" &&
              JSON.parse(description).means === "ACCOUNT"
                ? {
                    key: t(`${COMPONENT_LOCALE_KEY}content.walletNo`),
                    value: JSON.parse(description).octopusNo,
                  }
                : {
                    key: t(`${COMPONENT_LOCALE_KEY}content.cardNo`),
                    value:
                      cardType === "OCTOPUS"
                        ? JSON.parse(description).octopusNo
                        : first6Last4,
                  }),
            },
            ...(payment
              ? [
                  {
                    key: t(`${COMPONENT_LOCALE_KEY}content.cardOrigin`),
                    value: payment?.cardOrigin,
                  },
                  {
                    key: t(
                      `${COMPONENT_LOCALE_KEY}content.cardIssuingCountryCode`,
                    ),
                    value: payment?.cardIssuingCountryCode,
                  },
                  {
                    key: t(`${COMPONENT_LOCALE_KEY}content.cardIssuingBank`),
                    value: payment?.cardIssuingBank,
                  },
                ]
              : []),
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.aid`),
              value: aid,
            },
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.entryMode`),
              value: entryMode,
            },
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.batchNumber`),
              value: batchNumber,
            },
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.rrn`),
              value:
                payment_gateway === "OCTOPUS"
                  ? JSON.parse(description).octopusRefNo
                  : rrn,
            },
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.paymentGateway`),
              value: paymentGateway,
            },
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.stan`),
              value: stan,
            },
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.authCode`),
              value: authCode,
            },
          ]}
        />
      );
    } else {
      //========================== Online ==========================
      return (
        <PaymentDetailsGrid
          items={[
            ...(!!openMainPayment
              ? [
                  {
                    key: t(`${COMPONENT_LOCALE_KEY}content.orderId`),
                    value: (
                      <div className={s.actionBtnContainer}>
                        <button
                          onClick={() => {
                            openMainPayment(order_id);
                            !!closeDialog && closeDialog();
                          }}
                        >
                          {order_id}
                        </button>
                      </div>
                    ),
                  },
                ]
              : []),
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.amount`),
              value: `${currency ?? ""} ${checkMoneyDataForTable(
                transaction,
                "amount",
              )}`,
            },
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.transactionTime`),
              value: dateTimeFormatter(time),
            },
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.responseCode`),
              value: `${responseCode ?? ""}${
                responseCodeMapping[responseCode]?.["en"]
                  ? ` (${responseCodeMapping[responseCode]?.["en"]})`
                  : ""
              }`,
            },
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.cardNo`),
              value: first6Last4,
            },
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.paymentGateway`),
              value: payment_gateway,
            },
            {
              key: t(`${COMPONENT_LOCALE_KEY}content.stan`),
              value: stan,
            },
          ]}
        />
      );
    }
  };

  const getTransactionDetailsActions = (transaction) => {
    return (
      <>
        {openSendReceiptDialog && (
          <ButtonMAS
            variant="primary"
            size="md"
            label={t(COMPONENT_LOCALE_KEY + "action.receipt")}
            disabled={isReceiptDisabled(transaction)}
            onClick={() => openSendReceiptDialog(transaction)}
          />
        )}
        {openRefundTransactionDialog && (
          <ButtonMAS
            variant="primary"
            size="md"
            label={t(COMPONENT_LOCALE_KEY + "action.void")}
            disabled={isVoidDisabled(transaction, payment)}
            onClick={() => openVoidTransactionDialog(transaction)}
          />
        )}
        {openRefundTransactionDialog && (
          <ButtonMAS
            variant="primary"
            size="md"
            label={t(COMPONENT_LOCALE_KEY + "action.refund")}
            disabled={isRefundDisabled(transaction, payment)}
            onClick={() => openRefundTransactionDialog(transaction)}
          />
        )}
      </>
    );
  };

  const isReceiptDisabled = (transaction) => {
    if (!transaction) return true;

    if (!["SUCCESS", "SETTLED"].includes(transaction.status)) return true;

    if (transaction.transactionType.includes("REVERSAL")) return true;

    return false;
  };

  const isVoidDisabled = (transaction, payment) => {
    if (!transaction || !payment) return true;

    // Exceeded 1 day cannot be voided
    // moment().format will convert to local time UTC+8
    if (
      hasPassedNextDayMidnight(
        moment(transaction.time).format("YYYY-MM-DD HH:mm:ss Z"),
      )
    )
      return true;

    if (["VOIDED", "REFUNDED"].includes(payment.status)) return true;

    if (transaction.status !== "SUCCESS") return true;

    if (isOffline) {
      if (transaction.paymentGateway.startsWith("OCTOPUS")) return true;

      // Dimmed the auth trx void button when auth_complete exist.
      if(["AUTH"].includes(transaction.transactionType) && ["SUCCESS"].includes(payment.status)) return true;

      if (!["SALE", "AUTH", "AUTH_COMPLETE"].includes(transaction.transactionType)) return true;
    } else {
      if (transaction.transactionType !== "SALE") return true;
    }

    return false;
  };

  const isRefundDisabled = (transaction, payment) => {
    if (!payment || !transaction) return true;

    // not exceeded 1 day cannot be refunded
    if (
      !hasPassedNextDayMidnight(
        moment(transaction.time).format("YYYY-MM-DD HH:mm:ss Z"),
      )
    )
      return true;

    if (
      transaction.status !== "SETTLED" &&
      !["WECHAT", "ALIPAY"].includes(transaction.cardType)
    )
      return true;

    if (isOffline) {
      if (transaction.paymentGateway.startsWith("OCTOPUS")) return true;

      if (!["SALE", "AUTH", "AUTH_COMPLETE"].includes(transaction.transactionType)) return true;

      if (payment.netAmount <= 0) return true;
      if (payment.netAmount > 0) {
        // only allow 1 time partial refund
        if (
          ["YEDPAY"].includes(transaction.paymentGateway) &&
          payment.status === "REFUNDED"
        )
          return true;
      }
    } else {
      if (transaction.transactionType !== "SALE") return true;
    }

    return false;
  };

  return (
    <div className={s["transaction-card"]}>
      <div className={s[`header-info-container${dialog ? "-dialog" : ""}`]}>
        <Stack alignItems="center" direction="row" gap={2}>
          {(!isOffline || dialog) &&
            (getCardIconComponent(transaction) !== "-"
              ? getCardIconComponent(transaction, {
                  width: 48,
                  height: 32,
                })
              : payment?.cardType)}
          <Typography variant="content-xl">
            {t(transaction.transactionType ?? "") ??
              transactionType.replaceAll("_", " ")}{" "}
            {`(#${transaction.id})`}
          </Typography>
        </Stack>

        <PaymentDetailTag status={transaction.status} />
      </div>
      <div className={s[`card-content-container${dialog ? "-dialog" : ""}`]}>
        <div className={s["section-container"]}>
          {getTransactionDetailContent(transaction, payment)}
        </div>
        <div className={s["card-action"]}>
          {getTransactionDetailsActions(transaction)}
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailCard;
